import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { format } from 'date-fns';
import type DeskModel from 'garaje/models/desk';
import type MapFloorModel from 'garaje/models/map-floor';
import type WorkplaceDayModel from 'garaje/models/workplace-day';
import type AjaxService from 'garaje/services/ajax';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type LoggerService from 'garaje/services/logger';
import urlBuilder from 'garaje/utils/url-builder';

type Phase = 'desk-selection' | 'move-details';

interface CurrentDeskInfo {
  name: string;
  floorName: string | null;
  neighborhoodName: string | null;
}

interface MoveRequestModalArgs {
  isOpen: boolean;
  onClose: () => void;
  onSubmitted: () => void;
  currentDesk: CurrentDeskInfo | null;
  hasCurrentDesk: boolean;
  floors: MapFloorModel[];
  currentFloorId: string | null;
  employeeId: string;
  locationId: string;
  timezone: string;
  workplaceDays?: WorkplaceDayModel[];
}

export default class MoveRequestModalComponent extends Component<{ Args: MoveRequestModalArgs }> {
  @service declare ajax: AjaxService;
  @service declare flashMessages: FlashMessagesService;
  @service declare logger: LoggerService;

  @tracked phase: Phase = 'desk-selection';
  @tracked selectedDesk: DeskModel | null = null;
  @tracked selectedFloorId: string | null = null;
  @tracked moveDate: Date | null = null;
  @tracked moveRequestReason = '';
  @tracked isSubmitting = false;

  get isDeskSelectionPhase(): boolean {
    return this.phase === 'desk-selection';
  }

  get isMoveDetailsPhase(): boolean {
    return this.phase === 'move-details';
  }

  get canProceedToDetails(): boolean {
    return this.selectedDesk !== null;
  }

  get canSubmit(): boolean {
    return this.moveDate != null && this.moveRequestReason.trim().length > 0;
  }

  get selectedDeskDisplayText(): string {
    if (!this.selectedDesk) return '';
    const name = this.selectedDesk.name;
    const floorId = this.selectedDesk.belongsTo('floor').id();
    const floor = floorId ? this.selectedDesk.store.peekRecord('floor', floorId) : null;
    return [name, floor?.name].filter(Boolean).join(', ');
  }

  get selectedDeskNeighborhood(): string | null {
    return this.selectedDesk?.neighborhoodName ?? null;
  }

  get currentDeskDisplayText(): string {
    if (!this.args.currentDesk) return '';
    return [this.args.currentDesk.name, this.args.currentDesk.floorName].filter(Boolean).join(', ');
  }

  get currentDeskNeighborhood(): string | null {
    return this.args.currentDesk?.neighborhoodName ?? null;
  }

  get buttonText(): string {
    return this.args.hasCurrentDesk ? 'Request desk change' : 'Request desk assignment';
  }

  get title(): string {
    return this.args.hasCurrentDesk ? 'Request desk change' : 'Request desk assignment';
  }

  /**
   * Returns the working hours start time (e.g., "08:00:00") for the selected move date,
   * or "00:00:00" if no active workplace day is configured for that day.
   */
  get moveDateStartTime(): string {
    if (!this.moveDate) return '00:00:00';
    const dayOfWeek = format(this.moveDate, 'EEEE');
    const activeDay = this.args.workplaceDays?.find((d) => d.active && d.dayOfWeek === dayOfWeek);
    return activeDay?.startTime ?? '00:00:00';
  }

  get activeFloorId(): string | null {
    return this.selectedFloorId ?? this.args.currentFloorId;
  }

  @action
  handleFloorChange(floorId: string): void {
    this.selectedFloorId = floorId;
  }

  @action
  handleDeskSelect(desk: DeskModel | null): void {
    this.selectedDesk = desk;
  }

  @action
  goToNextPhase(): void {
    if (this.canProceedToDetails) {
      this.phase = 'move-details';
    }
  }

  @action
  goToPreviousPhase(): void {
    this.phase = 'desk-selection';
  }

  @action
  handleClose(): void {
    this.resetState();
    this.args.onClose();
  }

  @action
  updateMoveDate(date: Date | null): void {
    if (date) {
      this.moveDate = date;
    }
  }

  @action
  updateMoveRequestReason(event: Event): void {
    this.moveRequestReason = (event.target as HTMLInputElement).value;
  }

  @action
  async submitRequest(): Promise<void> {
    if (!this.canSubmit || !this.selectedDesk || this.isSubmitting) return;

    this.isSubmitting = true;
    try {
      // moveDateTime is in the location's local timezone (passed separately as `timezone`).
      // The backend interprets this datetime using the timezone field.
      const moveDateTime = `${format(this.moveDate!, 'yyyy-MM-dd')}T${this.moveDateStartTime}`;
      await this.ajax.request(urlBuilder.moveRequests.createUrl(), {
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify({
          employeeId: Number(this.args.employeeId),
          deskId: Number(this.selectedDesk?.id),
          locationId: Number(this.args.locationId),
          moveDateTime,
          timezone: this.args.timezone,
          reason: this.moveRequestReason.trim(),
        }),
      });
      this.resetState();
      this.args.onSubmitted();
    } catch (error) {
      this.logger.error(error instanceof Error ? error : new Error('Failed to create move request'));
      this.flashMessages.showAndHideFlash('error', 'Unable to create move request. Please try again.', {
        showAboveModalOverlay: true,
      });
    } finally {
      this.isSubmitting = false;
    }
  }

  private resetState(): void {
    this.phase = 'desk-selection';
    this.selectedDesk = null;
    this.selectedFloorId = null;
    this.moveDate = null;
    this.moveRequestReason = '';
    this.isSubmitting = false;
  }
}
